import { useContext } from "react";
import {
    Alert,
    Button,
    Card,
    Col,
    Collapse,
    Divider,
    Form,
    Progress,
    Row,
    Table,
    Tabs,
    Tag,
    Typography,
    Space,
    Empty,
} from "antd";
import {
    InboxOutlined,
    CloudUploadOutlined,
    DownloadOutlined,
    FileTextOutlined,
    CodeOutlined,
    CheckCircleOutlined,
    ClockCircleOutlined,
    FileDoneOutlined,
} from "@ant-design/icons";
import { Controller } from "react-hook-form";
import { UploadChangeParam, UploadFile } from "antd/es/upload";
import Dragger from "antd/es/upload/Dragger";

import useImportInvoiceForm from "./useImportInvoiceForm";
import { formatDateStringToDateAndTime } from "../../../../utils/Conversores";
import { ThemeContext } from "../../../../contexts/ThemeContext";

const { Text } = Typography;

export default function ImportInvoiceForm() {
    const {
        handleSubmitForm,
        handleSubmit,
        control,
        isSubmiting,
        errors,
        ACCEPTED_FILE_TYPES,
        dummyRequest,
        handleChangeFile,
        files,
        pendingInvoicesFile,
        handleClickDownloadFile,
    } = useImportInvoiceForm();

    const { theme } = useContext<any>(ThemeContext);
    const isDark = theme === "dark";

    const cardBg = isDark ? "#141414" : "#ffffff";
    const cardBorder = isDark ? "#303030" : "#e2e8f0";

    const getFileStatusTag = (status?: string) => {
        if (!status) return <Tag>—</Tag>;
        const s = status.toLowerCase();
        if (s.includes("finaliz") || s.includes("conclu") || s.includes("sucess")) {
            return (
                <Tag color="success" icon={<CheckCircleOutlined />} style={{ borderRadius: 4, fontWeight: 600 }}>
                    {status}
                </Tag>
            );
        }
        if (s.includes("process") || s.includes("andamento") || s.includes("aguard")) {
            return (
                <Tag color="processing" icon={<ClockCircleOutlined />} style={{ borderRadius: 4, fontWeight: 600 }}>
                    {status}
                </Tag>
            );
        }
        return <Tag color="default" style={{ borderRadius: 4, fontWeight: 600 }}>{status}</Tag>;
    };

    return (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <Card
                style={{
                    background: cardBg,
                    border: `1px solid ${cardBorder}`,
                    borderRadius: 10,
                }}
                bodyStyle={{ padding: "16px 20px" }}
            >
                <div style={{ marginBottom: 14 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <CloudUploadOutlined style={{ color: "#1677ff", fontSize: 18 }} />
                        <span style={{ fontSize: 15, fontWeight: 700, color: isDark ? "#f0f0f0" : "#0f172a" }}>
                            Upload de Arquivos XML
                        </span>
                    </div>
                    <div style={{ fontSize: 12, color: isDark ? "#8c8c8c" : "#64748b", marginTop: 2 }}>
                        Selecione ou arraste os arquivos XML das notas fiscais para processamento e importação em lote.
                    </div>
                </div>

                <Form
                    onFinish={handleSubmit(handleSubmitForm)}
                    autoComplete="off"
                    layout="vertical"
                >
                    <Form.Item
                        required
                        validateStatus={errors.files ? "error" : undefined}
                        help={errors.files?.message}
                        style={{ marginBottom: 14 }}
                    >
                        <Controller
                            name="files"
                            control={control}
                            rules={{
                                required: {
                                    value: true,
                                    message: "Por favor, selecione pelo menos um arquivo XML!",
                                },
                            }}
                            render={({ field }) => (
                                <Dragger
                                    {...field}
                                    fileList={files}
                                    showUploadList={true}
                                    customRequest={dummyRequest}
                                    multiple
                                    onChange={(e: UploadChangeParam<UploadFile>) =>
                                        handleChangeFile({
                                            onChangeHookForm: field.onChange,
                                            fileList: e.fileList,
                                        })
                                    }
                                    accept={ACCEPTED_FILE_TYPES.toString()}
                                    style={{
                                        background: isDark ? "#1f1f1f" : "#f8fafc",
                                        borderRadius: 8,
                                        padding: "16px 0",
                                        border: `1.5px dashed ${isDark ? "#434343" : "#cbd5e1"}`,
                                    }}
                                >
                                    <p className="ant-upload-drag-icon" style={{ marginBottom: 8 }}>
                                        <InboxOutlined style={{ color: "#1677ff", fontSize: 42 }} />
                                    </p>
                                    <div style={{ fontWeight: 600, fontSize: 14, color: isDark ? "#f0f0f0" : "#1e293b" }}>
                                        Clique ou arraste arquivos XML aqui para fazer upload
                                    </div>
                                    <div style={{ fontSize: 12, color: isDark ? "#8c8c8c" : "#64748b", marginTop: 4 }}>
                                        Suporte a envio múltiplo de arquivos no formato <strong>.xml</strong>
                                    </div>
                                    {files && files.length > 0 && (
                                        <div style={{ marginTop: 10 }}>
                                            <Tag color="blue" style={{ borderRadius: 6, fontWeight: 600, padding: "2px 8px" }}>
                                                {files.length} {files.length === 1 ? "arquivo selecionado" : "arquivos selecionados"}
                                            </Tag>
                                        </div>
                                    )}
                                </Dragger>
                            )}
                        />
                    </Form.Item>

                    <div style={{ display: "flex", justifyContent: "flex-end" }}>
                        <Button
                            type="primary"
                            icon={<CloudUploadOutlined />}
                            loading={isSubmiting}
                            disabled={isSubmiting || !files || files.length === 0}
                            onClick={handleSubmit(handleSubmitForm)}
                            style={{ minWidth: 140, height: 36, fontWeight: 600, borderRadius: 6 }}
                        >
                            Importar Arquivos
                        </Button>
                    </div>
                </Form>
            </Card>
            <div>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
                    <Space size={8} align="center">
                        <FileDoneOutlined style={{ color: "#52c41a", fontSize: 16 }} />
                        <span style={{ fontSize: 14, fontWeight: 700, color: isDark ? "#f0f0f0" : "#0f172a" }}>
                            Arquivos Importados
                        </span>
                        <Tag color="cyan" style={{ borderRadius: 6, fontWeight: 600, fontSize: 11, margin: 0 }}>
                            {pendingInvoicesFile.length} {pendingInvoicesFile.length === 1 ? "arquivo" : "arquivos"}
                        </Tag>
                    </Space>
                </div>

                {pendingInvoicesFile.length === 0 ? (
                    <Card
                        style={{
                            background: cardBg,
                            border: `1px solid ${cardBorder}`,
                            borderRadius: 10,
                            textAlign: "center",
                        }}
                    >
                        <Empty
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                            description={
                                <span style={{ color: isDark ? "#8c8c8c" : "#94a3b8" }}>
                                    Nenhum lote importado recentemente.
                                </span>
                            }
                        />
                    </Card>
                ) : (
                    <Row gutter={[12, 12]}>
                        {pendingInvoicesFile.map((file, index) => {
                            const displayName = file.file_name?.split("-")[3] || file.file_name || `Arquivo ${index + 1}`;
                            const isDone = file.status === "Finalizado" || file.import_progress >= 100;

                            return (
                                <Col key={file.id} xs={24} sm={12} lg={8}>
                                    <Card
                                        size="small"
                                        style={{
                                            background: cardBg,
                                            border: `1px solid ${cardBorder}`,
                                            borderRadius: 8,
                                            height: "100%",
                                        }}
                                        title={
                                            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                                                <div style={{ display: "flex", alignItems: "center", gap: 6, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                                    {getFileStatusTag(file.status)}
                                                    <span style={{ fontSize: 12.5, fontWeight: 600, color: isDark ? "#f0f0f0" : "#0f172a" }}>
                                                        {displayName}
                                                    </span>
                                                </div>
                                                <Button
                                                    type="default"
                                                    size="small"
                                                    icon={<DownloadOutlined />}
                                                    onClick={() => handleClickDownloadFile(file.file_name)}
                                                    style={{ fontSize: 11, borderRadius: 4 }}
                                                >
                                                    Baixar
                                                </Button>
                                            </div>
                                        }
                                    >
                                        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 11 }}>
                                                <span style={{ color: isDark ? "#8c8c8c" : "#64748b" }}>Progresso:</span>
                                                <strong>{file.import_progress ? `${file.import_progress.toFixed(1)}%` : "0%"}</strong>
                                            </div>
                                            <Progress
                                                percent={file.import_progress ? Number(file.import_progress.toFixed(1)) : 0}
                                                status={isDone ? "success" : "active"}
                                                size="small"
                                            />
                                            <div style={{ fontSize: 11, color: isDark ? "#8c8c8c" : "#94a3b8", marginTop: 2 }}>
                                                Criado em: {file.created_at ? formatDateStringToDateAndTime(file.created_at, true) : "—"}
                                            </div>
                                        </div>
                                    </Card>
                                </Col>
                            );
                        })}
                    </Row>
                )}
            </div>
            <Collapse
                style={{
                    background: cardBg,
                    border: `1px solid ${cardBorder}`,
                    borderRadius: 10,
                }}
                items={[
                    {
                        key: "format",
                        label: (
                            <Space size={6} align="center">
                                <CodeOutlined style={{ color: "#1677ff" }} />
                                <span style={{ fontWeight: 600, fontSize: 13 }}>
                                    Formato e Estrutura Esperada dos Arquivos (XML / JSON)
                                </span>
                            </Space>
                        ),
                        children: (
                            <Tabs
                                items={[
                                    {
                                        key: "xml",
                                        label: (
                                            <Space size={4}>
                                                <FileTextOutlined />
                                                <span>XML</span>
                                            </Space>
                                        ),
                                        children: (
                                            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                                                <Alert
                                                    type="info"
                                                    showIcon
                                                    message="O arquivo XML deve conter as colunas exatamente nesta sequência:"
                                                    style={{ borderRadius: 6 }}
                                                />

                                                <Table
                                                    size="small"
                                                    pagination={false}
                                                    columns={[
                                                        {
                                                            title: "#",
                                                            dataIndex: "index",
                                                            width: 60,
                                                            align: "center",
                                                        },
                                                        {
                                                            title: "Campo esperado",
                                                            dataIndex: "field",
                                                        },
                                                    ]}
                                                    dataSource={[
                                                        { index: 1, field: "Número" },
                                                        { index: 2, field: "Data de Emissão" },
                                                        { index: 3, field: "Prestador - Nome do Prestador" },
                                                        { index: 4, field: "Prestador - CPF / CNPJ" },
                                                        { index: 5, field: "Tomador - Nome Razão" },
                                                        { index: 6, field: "Tomador - CPF / CNPJ" },
                                                        { index: 7, field: "Valores da NFE - Total" },
                                                        { index: 8, field: "Ambiente Nacional - Chave de Acesso" },
                                                    ]}
                                                />

                                                <Divider style={{ margin: "8px 0" }} />

                                                <Text strong style={{ fontSize: 12.5 }}>
                                                    Exemplo da estrutura XML:
                                                </Text>

                                                <pre
                                                    style={{
                                                        margin: 0,
                                                        padding: 14,
                                                        background: isDark ? "#1f1f1f" : "#f8fafc",
                                                        border: `1px solid ${cardBorder}`,
                                                        borderRadius: 6,
                                                        overflow: "auto",
                                                        fontFamily: "monospace",
                                                        fontSize: 11.5,
                                                        color: isDark ? "#e2e8f0" : "#334155",
                                                    }}
                                                >{`<relatorio>
  <linha>
    <coluna><titulo>Número</titulo></coluna>
    <coluna><titulo>Data de Emissão</titulo></coluna>
    <coluna><titulo>Prestador - Nome do Prestador</titulo></coluna>
    <coluna><titulo>Prestador - CPF / CNPJ</titulo></coluna>
    <coluna><titulo>Tomador - Nome Razão</titulo></coluna>
    <coluna><titulo>Tomador - CPF / CNPJ</titulo></coluna>
    <coluna><titulo>Valores da NFE - Total</titulo></coluna>
    <coluna><titulo>Ambiente Nacional - Chave de Acesso</titulo></coluna>
  </linha>

  <linha>
    <coluna><dados>8373</dados></coluna>
    <coluna><dados>25/06/2026</dados></coluna>
    <coluna><dados>SGS DO BRASIL LTDA</dados></coluna>
    <coluna><dados>33.182.809/0004-83</dados></coluna>
    <coluna><dados>GUARUFERTIL COMERCIAL LTDA</dados></coluna>
    <coluna><dados>58.921.342/0001-24</dados></coluna>
    <coluna><dados>171,00</dados></coluna>
    <coluna><dados>411820...</dados></coluna>
  </linha>
</relatorio>`}
                                                </pre>
                                            </div>
                                        ),
                                    },
                                    {
                                        key: "json",
                                        label: (
                                            <Space size={4}>
                                                <CodeOutlined />
                                                <span>JSON</span>
                                            </Space>
                                        ),
                                        children: (
                                            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                                                <Alert
                                                    type="info"
                                                    showIcon
                                                    message="O arquivo JSON deve ser um array de objetos contendo exatamente as seguintes propriedades:"
                                                    style={{ borderRadius: 6 }}
                                                />

                                                <Table
                                                    size="small"
                                                    pagination={false}
                                                    columns={[
                                                        {
                                                            title: "Propriedade",
                                                            dataIndex: "field",
                                                        },
                                                    ]}
                                                    dataSource={[
                                                        { field: "Número" },
                                                        { field: "Data de Emissão" },
                                                        { field: "Prestador - Nome do Prestador" },
                                                        { field: "Prestador - CPF / CNPJ" },
                                                        { field: "Tomador - Nome Razão" },
                                                        { field: "Tomador - CPF / CNPJ" },
                                                        { field: "Valores da NFE - Total" },
                                                        { field: "Ambiente Nacional - Chave de Acesso" },
                                                    ]}
                                                />

                                                <Divider style={{ margin: "8px 0" }} />

                                                <Text strong style={{ fontSize: 12.5 }}>
                                                    Exemplo de JSON:
                                                </Text>

                                                <pre
                                                    style={{
                                                        margin: 0,
                                                        padding: 14,
                                                        background: isDark ? "#1f1f1f" : "#f8fafc",
                                                        border: `1px solid ${cardBorder}`,
                                                        borderRadius: 6,
                                                        overflow: "auto",
                                                        fontFamily: "monospace",
                                                        fontSize: 11.5,
                                                        color: isDark ? "#e2e8f0" : "#334155",
                                                    }}
                                                >{`[
  {
    "Número": "8373",
    "Data de Emissão": "25/06/2026",
    "Prestador - Nome do Prestador": "SGS DO BRASIL LTDA",
    "Prestador - CPF / CNPJ": "33.182.809/0004-83",
    "Tomador - Nome Razão": "GUARUFERTIL COMERCIAL LTDA",
    "Tomador - CPF / CNPJ": "58.921.342/0001-24",
    "Valores da NFE - Total": "171,00",
    "Ambiente Nacional - Chave de Acesso": "41182041233182809000483000000000837326060000000010"
  }
]`}
                                                </pre>
                                            </div>
                                        ),
                                    },
                                ]}
                            />
                        ),
                    },
                ]}
            />
        </div>
    );
}
